@if (loadingAssignments()) {
	<div class="assignment-skeleton-container">
		@for (row of [1]; track row) {
			<div class="assignment-skeleton">
				<div class="assignment-skeleton__title"></div>
				<div class="assignment-skeleton__subtitle"></div>
			</div>
		}
	</div>
} @else {
	<cdk-accordion class="assignment-accordion" [multi]="true">
		<!-- Active Assignment -->
		@for (item of activeAssignments(); track item; let index = $index) {
			<!-- Draft assignment -->
			@if (isDraft(item)) {
				<lvr-assignment-accordion-item
					[showToggle]="false"
					[temporary]="true"
					[toggleOnIconOnly]="toggleOnIconOnly()"
					[assignmentId]="item.id"
					[phaseId]="item.phaseId"
					[disableTitleNavigation]="item.isRestricted"
					[disableTitleNavigationTooltip]="'base.button.assignment_details_disabled' | transloco"
				>
					@let draftTitle = getAccordionTitleKey(item);
					<span ngProjectAs="accordion-title">{{ draftTitle.key | transloco: draftTitle.params }}</span>
					<span ngProjectAs="accordion-date"></span>
					<div ngProjectAs="accordion-button" style="display: flex; justify-content: space-between">
						<div style="display: inline-flex; gap: 1rem">
							@let phaseTooltipKey = getPhaseTransitionDisabledTooltipKey();

							<span
								style="display: inline-flex"
								[matTooltip]="phaseTooltipKey ? (phaseTooltipKey | transloco) : ''"
								[matTooltipDisabled]="!phaseTooltipKey"
							>
								<lvr-button
									variant="primary"
									[label]="(item.draftPhaseId === DB_PHASES.P4 ? 'assignment.end' : 'assignment.start') | transloco"
									[disabled]="isPhaseTransitionDisabledForAssignment(item)"
									[type]="'button'"
									(clicked)="triggerPhaseMove($event, item)"
									icon="airline_stops"
								></lvr-button>
							</span>

							<lvr-button
								variant="primary"
								[label]="'base.button.delete' | transloco"
								[disabled]="isRepresentativeMode() || !isAssignmentOwner(item) || !this.canTransitionPhases()"
								[type]="'button'"
								(clicked)="deleteDraft($event, item)"
								icon="delete"
							></lvr-button>
						</div>
					</div>
				</lvr-assignment-accordion-item>
			}

			<!-- Default behavior -->
			<lvr-assignment-accordion-item
				[expanded]="shouldOpenMain(item)"
				[toggleOnIconOnly]="toggleOnIconOnly()"
				[assignmentId]="item.id"
				[phaseId]="item.phaseId"
				[disableTitleNavigation]="item.isRestricted"
				[disableTitleNavigationTooltip]="'base.button.assignment_details_disabled' | transloco"
				[chipLabel]="
					!isMainOwner(item)
						? (item.createdByUser?.displayName ?? ('assignment.additional.unknown_user' | transloco))
						: null
				"
			>
				@let mainTitle = getMainAccordionTitleKey(item);
				@let mainSponsor = getSponsorName(item);
				<span ngProjectAs="accordion-title">{{ mainTitle.key | transloco: mainTitle.params }}</span>
				<span ngProjectAs="accordion-date"
					>{{ getPhaseStartDate(item) | date: 'dd.MM.yyyy' }}
					@if (mainSponsor) {
						| {{ mainSponsor }}
					}
				</span>
				<div ngProjectAs="accordion-button" style="display: flex; justify-content: space-between">
					@if (!item.isRestricted) {
						@if (item.phaseId === DB_PHASES.P2 && !isAdmin()) {
							@let phaseTooltipKey = getPhaseTransitionDisabledTooltipKey();

							<span
								style="display: inline-flex"
								[matTooltip]="phaseTooltipKey ? (phaseTooltipKey | transloco) : ''"
								[matTooltipDisabled]="!phaseTooltipKey"
							>
								<lvr-button
									style="padding-right: 1rem"
									variant="primary"
									[label]="'assignment.transfer' | transloco"
									[disabled]="isPhaseTransitionDisabled()"
									[type]="'button'"
									(clicked)="triggerPhaseMove($event, item)"
									icon="airline_stops"
								></lvr-button>
							</span>
						}

						@if (item.phaseId !== 5) {
							@let cancelTooltipKey = getPhaseCancelDisabledTooltipKey();

							<span
								style="display: inline-flex"
								[matTooltip]="cancelTooltipKey ? (cancelTooltipKey | transloco) : ''"
								[matTooltipDisabled]="!cancelTooltipKey"
							>
								@if (isMainOwner(item)) {
									<lvr-button
										variant="primary"
										[label]="'assignment.end' | transloco"
										[disabled]="isPhaseEndDisabledForMainAssignment(item)"
										[type]="'button'"
										(clicked)="switchPhase($event, item)"
										icon="tab_close_right"
									></lvr-button>
								}
							</span>
						}
					}
				</div>
				@if (!item.isRestricted) {
					<section>
						<div class="contact-headline">
							<span>{{ 'contact.contact_view.all_contacts' | transloco: { count: getContactCount(item) } }}</span>
							@if (canAddContactToItem(item)) {
								<lvr-button
									variant="primary"
									[label]="'contact.assignment_accordion.contact' | transloco"
									[ariaLabel]="'contact.assignment_accordion.contact_hint' | transloco"
									[type]="'button'"
									(clicked)="openContactCreate(item)"
									icon="add"
									style="margin-left: 8px"
								></lvr-button>
							}
						</div>
						@if (isContactsLoading(item)) {
							<p class="contacts-state">Kontakte werden geladen...</p>
						} @else if (hasContactsError(item)) {
							<p class="contacts-state contacts-state-error">Kontakte konnten nicht geladen werden.</p>
						}
					</section>
					@for (
						contact of visibleContacts(item.id, item.contacts, 'assignment-accordion-active-assignments');
						track contact.id
					) {
						@if (canReadContactsRestricted()) {
							<lvr-contact-card
								[showEdit]="isMainOwner(item)"
								[contact]="contact"
								[showRemarks]="false"
								[showDocuments]="true"
								[clientId]="clientId()"
							></lvr-contact-card>
						} @else {
							<a class="link" [routerLink]="[item.id, 'contacts', contact.id]">
								<lvr-contact-card
									[showEdit]="isMainOwner(item)"
									[contact]="contact"
									[showRemarks]="false"
									[showDocuments]="true"
									[clientId]="clientId()"
								></lvr-contact-card>
							</a>
						}
					}
					@if (item.contacts.length > 0) {
						<div class="contacts-footer">
							<lvr-button
								variant="primary"
								[label]="'base.button.export_contacts' | transloco"
								[ariaLabel]="'base.button.export_pdf' | transloco"
								[loadingLabel]="'base.button.generating_pdf' | transloco"
								[loading]="isExportLoadingFor(item.id)()"
								[disabled]="!canExportContacts(item)"
								[type]="'button'"
								(clicked)="export(item.id)"
								icon="ios_share"
							></lvr-button>
							<lvr-paginator
								class="paginator"
								[showFirstLastButtons]="showFirstLastButtons"
								[length]="item.contacts.length"
								[pageIndex]="pageIndexFor(item.id, 'assignment-accordion-active-assignments')"
								[pageSize]="pageSizeFor(item.id, 'assignment-accordion-active-assignments')"
								preferenceKey="assignment-accordion-active-assignments"
								(page)="onContactsPage(item.id, $event, 'assignment-accordion-active-assignments')"
							/>
						</div>
					}
					<!-- Additional Assignments for phase 3 -->
					@if (isPhaseP3(item)) {
						@for (additional of additionalAssignmentsFor(item); track additional.id) {
							<lvr-section>
								<lvr-assignment-accordion-item
									[expanded]="shouldExpandAdditional(additional)"
									[toggleOnIconOnly]="toggleOnIconOnly()"
									[assignmentId]="additional.id"
									[phaseId]="additional.phaseId"
									[isAdditionalAssignment]="true"
									[disableTitleNavigation]="additional.isRestricted"
									[disableTitleNavigationTooltip]="'base.button.assignment_details_disabled' | transloco"
									[temporary]="additional.phaseId === DB_PHASES.P1"
									[chipLabel]="
										!isAdditionalOwner(additional)
											? (additional.ownerOfAdditionalAssignment?.displayName ??
												additional.createdByUser?.displayName ??
												('assignment.additional.unknown_user' | transloco))
											: null
									"
								>
									@let addTitle1 = getAccordionTitleKey(additional);
									@let addSponsor1 = getSponsorName(additional);
									<span ngProjectAs="accordion-title">{{ addTitle1.key | transloco: addTitle1.params }}</span>
									<span ngProjectAs="accordion-date"
										>{{ additional.careStartDate | date: 'dd.MM.yyyy' }}
										@if (addSponsor1) {
											| {{ addSponsor1 }}
										}
									</span>
									@if (isAdditionalOwner(additional)) {
										<div ngProjectAs="accordion-button" style="display: flex; justify-content: space-between">
											@if (additional.phaseId !== DB_PHASES.P3) {
												@let phaseTooltipKey = getPhaseTransitionDisabledTooltipKey();

												<span
													style="display: inline-flex"
													[matTooltip]="phaseTooltipKey ? (phaseTooltipKey | transloco) : ''"
													[matTooltipDisabled]="!phaseTooltipKey"
												>
													<lvr-button
														variant="primary"
														[label]="'assignment.start' | transloco"
														[disabled]="isPhaseTransitionDisabled()"
														[type]="'button'"
														(clicked)="triggerPhaseMove($event, additional)"
														icon="airline_stops"
													></lvr-button>
												</span>
											} @else {
												@let cancelTooltipKey = getPhaseCancelDisabledTooltipKey();

												<span
													style="display: inline-flex"
													[matTooltip]="cancelTooltipKey ? (cancelTooltipKey | transloco) : ''"
													[matTooltipDisabled]="!cancelTooltipKey"
												>
													<lvr-button
														variant="primary"
														[label]="'assignment.end' | transloco"
														[disabled]="isPhaseTransitionDisabled()"
														[type]="'button'"
														(clicked)="switchPhase($event, additional)"
														icon="tab_close_right"
													></lvr-button>
												</span>
											}
											@if (additional.phaseId === DB_PHASES.P1) {
												<lvr-button
													style="margin-left: 1rem"
													variant="primary"
													[label]="'assignment.additional.delete' | transloco"
													[ariaLabel]="'assignment.additional.delete' | transloco"
													[type]="'button'"
													[disabled]="!canDeleteAssignments()"
													(clicked)="deleteAdditional($event, additional)"
													icon="delete"
												></lvr-button>
											}
										</div>
									}
									<section>
										<div class="contact-headline">
											<span>{{
												'contact.contact_view.all_contacts' | transloco: { count: getContactCount(additional) }
											}}</span>
											@if (canAddContactToItem(additional)) {
												<lvr-button
													variant="primary"
													[label]="'contact.assignment_accordion.contact' | transloco"
													[ariaLabel]="'contact.assignment_accordion.contact_hint' | transloco"
													[type]="'button'"
													(clicked)="openContactCreate(additional)"
													icon="add"
													style="margin-left: 8px"
												></lvr-button>
											}
										</div>
										@if (isContactsLoading(additional)) {
											<p class="contacts-state">Kontakte werden geladen...</p>
										} @else if (hasContactsError(additional)) {
											<p class="contacts-state contacts-state-error">Kontakte konnten nicht geladen werden.</p>
										}
									</section>
									@for (
										contact of visibleContacts(
											additional.id,
											additional.contacts,
											'assignment-accordion-active-assignments-p3'
										);
										track contact.id
									) {
										@if (canReadContactsRestricted()) {
											<lvr-contact-card
												[showEdit]="canAddContactToItem(additional)"
												[contact]="contact"
												[showRemarks]="false"
												[showDocuments]="true"
												[clientId]="clientId()"
											></lvr-contact-card>
										} @else {
											<a class="link" [routerLink]="[additional.id, 'contacts', contact.id]">
												<lvr-contact-card
													[showEdit]="canAddContactToItem(additional)"
													[contact]="contact"
													[showRemarks]="false"
													[showDocuments]="true"
													[clientId]="clientId()"
												></lvr-contact-card>
											</a>
										}
									}
									@if (additional.contacts.length > 0) {
										<div class="contacts-footer">
											<lvr-button
												variant="primary"
												[label]="'base.button.export_contacts' | transloco"
												[ariaLabel]="'base.button.export_pdf' | transloco"
												[loadingLabel]="'base.button.generating_pdf' | transloco"
												[loading]="isExportLoadingFor(additional.id)()"
												[disabled]="!canExportContacts(additional)"
												[type]="'button'"
												(clicked)="export(additional.id)"
												icon="ios_share"
											></lvr-button>
											<lvr-paginator
												class="paginator"
												[showFirstLastButtons]="showFirstLastButtons"
												[length]="additional.contacts.length"
												[pageIndex]="pageIndexFor(additional.id, 'assignment-accordion-active-assignments-p3')"
												[pageSize]="pageSizeFor(additional.id, 'assignment-accordion-active-assignments-p3')"
												preferenceKey="assignment-accordion-active-assignments-p3"
												(page)="onContactsPage(additional.id, $event, 'assignment-accordion-active-assignments-p3')"
											/>
										</div>
									}
								</lvr-assignment-accordion-item>
							</lvr-section>
						}
					}
				} @else {
					<!-- Additional Assignments for phase 3 -->
					@if (isPhaseP3(item)) {
						@for (additional of additionalAssignmentsInRestrictedView(); track additional.id) {
							<lvr-section>
								<lvr-assignment-accordion-item
									[expanded]="shouldExpandAdditional(additional)"
									[toggleOnIconOnly]="toggleOnIconOnly()"
									[assignmentId]="additional.id"
									[phaseId]="additional.phaseId"
									[isAdditionalAssignment]="true"
									[disableTitleNavigation]="additional.isRestricted"
									[disableTitleNavigationTooltip]="'base.button.assignment_details_disabled' | transloco"
									[temporary]="additional.phaseId === DB_PHASES.P1"
									[chipLabel]="
										!isAdditionalOwner(additional)
											? (additional.ownerOfAdditionalAssignment?.displayName ??
												additional.createdByUser?.displayName ??
												('assignment.additional.unknown_user' | transloco))
											: null
									"
								>
									@let addTitle2 = getAccordionTitleKey(additional);
									@let addSponsor2 = getSponsorName(additional);
									<span ngProjectAs="accordion-title">{{ addTitle2.key | transloco: addTitle2.params }}</span>
									<span ngProjectAs="accordion-date"
										>{{ additional.careStartDate | date: 'dd.MM.yyyy' }}
										@if (addSponsor2) {
											| {{ addSponsor2 }}
										}
									</span>
									@if (isAdditionalOwner(additional)) {
										<div ngProjectAs="accordion-button" style="display: flex; justify-content: space-between">
											@if (additional.phaseId !== DB_PHASES.P3) {
												@let phaseTooltipKey = getPhaseTransitionDisabledTooltipKey();

												<span
													style="display: inline-flex"
													[matTooltip]="phaseTooltipKey ? (phaseTooltipKey | transloco) : ''"
													[matTooltipDisabled]="!phaseTooltipKey"
												>
													<lvr-button
														variant="primary"
														[label]="'assignment.start' | transloco"
														[disabled]="isPhaseTransitionDisabled()"
														[type]="'button'"
														(clicked)="triggerPhaseMove($event, additional)"
														icon="airline_stops"
													></lvr-button>
												</span>
											} @else {
												@let cancelTooltipKey = getPhaseCancelDisabledTooltipKey();

												<span
													style="display: inline-flex; justify-content: space-between"
													[matTooltip]="cancelTooltipKey ? (cancelTooltipKey | transloco) : ''"
													[matTooltipDisabled]="!cancelTooltipKey"
												>
													<lvr-button
														variant="primary"
														[label]="'assignment.end' | transloco"
														[disabled]="isPhaseTransitionDisabled()"
														[type]="'button'"
														(clicked)="switchPhase($event, additional)"
														icon="tab_close_right"
													></lvr-button>
												</span>
											}
											@if (additional.phaseId === DB_PHASES.P1) {
												<lvr-button
													style="margin-left: 1rem"
													variant="primary"
													[label]="'assignment.additional.delete' | transloco"
													[ariaLabel]="'assignment.additional.delete' | transloco"
													[type]="'button'"
													[disabled]="!canDeleteAssignments()"
													(clicked)="deleteAdditional($event, additional)"
													icon="delete"
												></lvr-button>
											}
										</div>
									}
									<section>
										<div class="contact-headline">
											<span>{{
												'contact.contact_view.all_contacts' | transloco: { count: getContactCount(additional) }
											}}</span>
											@if (canAddContactToItem(additional)) {
												<lvr-button
													variant="primary"
													[label]="'contact.assignment_accordion.contact' | transloco"
													[ariaLabel]="'contact.assignment_accordion.contact_hint' | transloco"
													[type]="'button'"
													(clicked)="openContactCreate(additional)"
													icon="add"
													style="margin-left: 8px"
												></lvr-button>
											}
										</div>
										@if (isContactsLoading(additional)) {
											<p class="contacts-state">Kontakte werden geladen...</p>
										} @else if (hasContactsError(additional)) {
											<p class="contacts-state contacts-state-error">Kontakte konnten nicht geladen werden.</p>
										}
									</section>
									@for (
										contact of visibleContacts(
											additional.id,
											additional.contacts,
											'assignment-accordion-active-assignments-restricted'
										);
										track contact.id
									) {
										@if (canReadContactsRestricted()) {
											<lvr-contact-card
												[showEdit]="canAddContactToItem(additional)"
												[contact]="contact"
												[showRemarks]="false"
												[showDocuments]="true"
												[clientId]="clientId()"
											></lvr-contact-card>
										} @else {
											<a class="link" [routerLink]="[additional.id, 'contacts', contact.id]">
												<lvr-contact-card
													[showEdit]="canAddContactToItem(additional)"
													[contact]="contact"
													[showRemarks]="false"
													[showDocuments]="true"
													[clientId]="clientId()"
												></lvr-contact-card>
											</a>
										}
									}
									@if (additional.contacts.length > 0) {
										<div class="contacts-footer">
											<lvr-button
												variant="primary"
												[label]="'base.button.export_contacts' | transloco"
												[ariaLabel]="'base.button.export_pdf' | transloco"
												[loadingLabel]="'base.button.generating_pdf' | transloco"
												[loading]="isExportLoadingFor(additional.id)()"
												[disabled]="!canExportContacts(additional)"
												[type]="'button'"
												(clicked)="export(additional.id)"
												icon="ios_share"
											></lvr-button>
											<lvr-paginator
												class="paginator"
												[showFirstLastButtons]="showFirstLastButtons"
												[length]="additional.contacts.length"
												[pageIndex]="pageIndexFor(additional.id, 'assignment-accordion-active-assignments-restricted')"
												[pageSize]="pageSizeFor(additional.id, 'assignment-accordion-active-assignments-restricted')"
												preferenceKey="assignment-accordion-active-assignments-restricted"
												(page)="
													onContactsPage(additional.id, $event, 'assignment-accordion-active-assignments-restricted')
												"
											/>
										</div>
									}
								</lvr-assignment-accordion-item>
							</lvr-section>
						}
					}
				}
			</lvr-assignment-accordion-item>
		}

		<!-- FDS Assignments -->
		@if (fdsAssignments().length > 0) {
			<mat-divider></mat-divider>
			<lvr-section>
				<h3 projectAs="lvr-title">{{ 'assignment.fds.title' | transloco }}</h3>
				@for (item of fdsAssignments(); track item; let index = $index) {
					<lvr-assignment-accordion-item
						[expanded]="shouldOpenMain(item)"
						[toggleOnIconOnly]="toggleOnIconOnly()"
						[assignmentId]="item.id"
						[phaseId]="item.phaseId"
						[isServiceOfficeStatement]="true"
						[disableTitleNavigation]="item.isRestricted"
						[disableTitleNavigationTooltip]="'base.button.assignment_details_disabled' | transloco"
						[chipLabel]="
							!isAssignmentOwner(item)
								? (item.createdByUser?.displayName ?? ('assignment.additional.unknown_user' | transloco))
								: null
						"
					>
						@let fdsTitle = getAccordionTitleKey(item);
						@let fdsSponsor = getSponsorName(item);
						<span ngProjectAs="accordion-title">{{ fdsTitle.key | transloco: fdsTitle.params }}</span>
						<span ngProjectAs="accordion-date"
							>{{ getPhaseStartDate(item) | date: 'dd.MM.yyyy' }}
							@if (fdsSponsor) {
								| {{ fdsSponsor }}
							}
						</span>

						@if (item.phaseId === 6 && isAssignmentOwner(item)) {
							<div ngProjectAs="accordion-button" style="display: flex; justify-content: space-between; gap: 18px">
								@let submitTooltipKey = getPhaseSubmitDisabledTooltipKey();

								<span
									style="display: inline-flex"
									[matTooltip]="submitTooltipKey ? (submitTooltipKey | transloco) : ''"
									[matTooltipDisabled]="!submitTooltipKey"
								>
									<lvr-button
										variant="primary"
										[label]="'assignment.fds.submit' | transloco"
										[disabled]="isPhaseTransitionDisabledForAssignment(item)"
										[type]="'button'"
										(clicked)="triggerPhaseMove($event, item)"
										icon="airline_stops"
									></lvr-button>
								</span>
								@let returnTooltipKey = getPhaseReturnDisabledTooltipKey();

								<span
									style="display: inline-flex"
									[matTooltip]="returnTooltipKey ? (returnTooltipKey | transloco) : ''"
									[matTooltipDisabled]="!returnTooltipKey"
								>
									<lvr-button
										variant="primary"
										[label]="'assignment.fds.return' | transloco"
										[disabled]="isPhaseTransitionDisabledForAssignment(item)"
										[type]="'button'"
										(clicked)="switchPhase($event, item)"
										icon="tab_close_right"
									></lvr-button>
								</span>
							</div>
						}
						<section>
							<div class="contact-headline">
								<span>{{ 'contact.contact_view.all_contacts' | transloco: { count: getContactCount(item) } }}</span>

								@if (canCreateContactForFds(item) && canAddContactToItem(item)) {
									<lvr-button
										variant="primary"
										[label]="'contact.assignment_accordion.contact' | transloco"
										[ariaLabel]="'contact.assignment_accordion.contact_hint' | transloco"
										[type]="'button'"
										(clicked)="openContactCreate(item)"
										icon="add"
										style="margin-left: 8px"
									></lvr-button>
								}
							</div>
							@if (isContactsLoading(item)) {
								<p class="contacts-state">Kontakte werden geladen...</p>
							} @else if (hasContactsError(item)) {
								<p class="contacts-state contacts-state-error">Kontakte konnten nicht geladen werden.</p>
							}
						</section>
						@for (contact of visibleContacts(item.id, item.contacts, 'assignment-accordion-fds'); track contact.id) {
							@if (canReadContactsRestricted()) {
								<lvr-contact-card
									[showEdit]="item.phaseId === 6 && isAssignmentOwner(item)"
									[contact]="contact"
									[showRemarks]="false"
									[showDocuments]="true"
									[clientId]="clientId()"
								></lvr-contact-card>
							} @else {
								<a class="link" [routerLink]="[item.id, 'contacts', contact.id]">
									<lvr-contact-card
										[showEdit]="item.phaseId === 6 && isAssignmentOwner(item)"
										[contact]="contact"
										[showRemarks]="false"
										[showDocuments]="true"
										[clientId]="clientId()"
									></lvr-contact-card>
								</a>
							}
						}
						@if (item.contacts.length > 0) {
							<div class="contacts-footer">
								<lvr-button
									variant="primary"
									[label]="'base.button.export_contacts' | transloco"
									[ariaLabel]="'base.button.export_pdf' | transloco"
									[loadingLabel]="'base.button.generating_pdf' | transloco"
									[loading]="isExportLoadingFor(item.id)()"
									[disabled]="!canExportContacts(item)"
									[type]="'button'"
									(clicked)="export(item.id)"
									icon="ios_share"
								></lvr-button>
								<lvr-paginator
									class="paginator"
									[showFirstLastButtons]="showFirstLastButtons"
									[length]="item.contacts.length"
									[pageIndex]="pageIndexFor(item.id, 'assignment-accordion-fds')"
									[pageSize]="pageSizeFor(item.id, 'assignment-accordion-fds')"
									preferenceKey="assignment-accordion-fds"
									(page)="onContactsPage(item.id, $event, 'assignment-accordion-fds')"
								/>
							</div>
						}
					</lvr-assignment-accordion-item>
				}
			</lvr-section>
		}

		<mat-divider></mat-divider>

		<!-- Finished Assignments -->
		<lvr-section>
			<h3 projectAs="lvr-title">{{ 'assignment.finished' | transloco }}</h3>
			@for (item of finishedAssignments(); track item; let index = $index) {
				<!-- Default behavior -->
				<lvr-assignment-accordion-item
					[expanded]="shouldOpenMain(item)"
					[toggleOnIconOnly]="toggleOnIconOnly()"
					[assignmentId]="item.id"
					[phaseId]="item.phaseId"
					[disableTitleNavigation]="item.isRestricted"
					[disableTitleNavigationTooltip]="'base.button.assignment_details_disabled' | transloco"
					[chipLabel]="
						!isMainOwner(item)
							? (item.createdByUser?.displayName ?? ('assignment.additional.unknown_user' | transloco))
							: null
					"
				>
					@let finTitle = getAccordionTitleKey(item);
					@let finSponsor = getSponsorName(item);
					<span ngProjectAs="accordion-title">{{ finTitle.key | transloco: finTitle.params }}</span>
					<span ngProjectAs="accordion-date"
						>{{ item.cancellationDate | date: 'dd.MM.yyyy' }}
						@if (finSponsor) {
							| {{ finSponsor }}
						}
					</span>
					<section>
						<div class="contact-headline">
							<span>{{ 'contact.contact_view.all_contacts' | transloco: { count: getContactCount(item) } }}</span>
						</div>
						@if (isContactsLoading(item)) {
							<p class="contacts-state">Kontakte werden geladen...</p>
						} @else if (hasContactsError(item)) {
							<p class="contacts-state contacts-state-error">Kontakte konnten nicht geladen werden.</p>
						}
					</section>
					@for (
						contact of visibleContacts(item.id, item.contacts, 'assignment-accordion-finished-assignments');
						track contact.id
					) {
						@if (canReadContactsRestricted()) {
							<lvr-contact-card [contact]="contact" [showRemarks]="false" [showDocuments]="true"></lvr-contact-card>
						} @else {
							<a class="link" [routerLink]="[item.id, 'contacts', contact.id]">
								<lvr-contact-card [contact]="contact" [showRemarks]="false" [showDocuments]="true"></lvr-contact-card>
							</a>
						}
					}
					@if (item.contacts.length > 0) {
						<div class="contacts-footer">
							<lvr-button
								variant="primary"
								[label]="'base.button.export_contacts' | transloco"
								[ariaLabel]="'base.button.export_pdf' | transloco"
								[loadingLabel]="'base.button.generating_pdf' | transloco"
								[loading]="isExportLoadingFor(item.id)()"
								[disabled]="!canExportContacts(item)"
								[type]="'button'"
								(clicked)="export(item.id)"
								icon="ios_share"
							></lvr-button>
							<lvr-paginator
								class="paginator"
								[showFirstLastButtons]="showFirstLastButtons"
								[length]="item.contacts.length"
								[pageIndex]="pageIndexFor(item.id, 'assignment-accordion-finished-assignments')"
								[pageSize]="pageSizeFor(item.id, 'assignment-accordion-finished-assignments')"
								preferenceKey="assignment-accordion-finished-assignments"
								(page)="onContactsPage(item.id, $event, 'assignment-accordion-finished-assignments')"
							/>
						</div>
					}
				</lvr-assignment-accordion-item>
			}
			@if (finishedAdditionalAssignments().length > 0) {
				@for (additional of finishedAdditionalAssignments(); track additional.id) {
					<lvr-assignment-accordion-item
						[expanded]="shouldOpenAdditional(additional)"
						[toggleOnIconOnly]="toggleOnIconOnly()"
						[assignmentId]="additional.id"
						[phaseId]="additional.phaseId"
						[isAdditionalAssignment]="true"
						[disableTitleNavigation]="additional.isRestricted"
						[disableTitleNavigationTooltip]="'base.button.assignment_details_disabled' | transloco"
						[chipLabel]="
							!isAdditionalOwner(additional)
								? (additional.ownerOfAdditionalAssignment?.displayName ??
									additional.createdByUser?.displayName ??
									('assignment.additional.unknown_user' | transloco))
								: null
						"
					>
						@let finAddTitle = getAccordionTitleKey(additional);
						@let finAddSponsor = getSponsorName(additional);
						<span ngProjectAs="accordion-title">{{ finAddTitle.key | transloco: finAddTitle.params }}</span>
						<span ngProjectAs="accordion-date"
							>{{ additional.cancellationDate | date: 'dd.MM.yyyy' }}
							@if (finAddSponsor) {
								| {{ finAddSponsor }}
							}
						</span>
						<section>
							<div class="contact-headline">
								<span>{{
									'contact.contact_view.all_contacts' | transloco: { count: getContactCount(additional) }
								}}</span>
							</div>
							@if (isContactsLoading(additional)) {
								<p class="contacts-state">Kontakte werden geladen...</p>
							} @else if (hasContactsError(additional)) {
								<p class="contacts-state contacts-state-error">Kontakte konnten nicht geladen werden.</p>
							}
						</section>
						@for (
							contact of visibleContacts(
								additional.id,
								additional.contacts,
								'assignment-accordion-finished-additional-assignments'
							);
							track contact.id
						) {
							@if (canReadContactsRestricted()) {
								<lvr-contact-card
									[showEdit]="additional.phaseId !== DB_PHASES.P4"
									[contact]="contact"
									[showRemarks]="false"
									[showDocuments]="true"
									[clientId]="clientId()"
								></lvr-contact-card>
							} @else {
								<a class="link" [routerLink]="[additional.id, 'contacts', contact.id]">
									<lvr-contact-card
										[showEdit]="additional.phaseId !== DB_PHASES.P4"
										[contact]="contact"
										[showRemarks]="false"
										[showDocuments]="true"
										[clientId]="clientId()"
									></lvr-contact-card>
								</a>
							}
						}
						@if (additional.contacts.length > 0) {
							<div class="contacts-footer">
								<lvr-button
									variant="primary"
									[label]="'base.button.export_contacts' | transloco"
									[ariaLabel]="'base.button.export_pdf' | transloco"
									[loadingLabel]="'base.button.generating_pdf' | transloco"
									[loading]="isExportLoadingFor(additional.id)()"
									[disabled]="!canExportContacts(additional)"
									[type]="'button'"
									(clicked)="export(additional.id)"
									icon="ios_share"
								></lvr-button>
								<lvr-paginator
									class="paginator"
									[showFirstLastButtons]="showFirstLastButtons"
									[length]="additional.contacts.length"
									[pageIndex]="pageIndexFor(additional.id, 'assignment-accordion-finished-additional-assignments')"
									[pageSize]="pageSizeFor(additional.id, 'assignment-accordion-finished-additional-assignments')"
									preferenceKey="assignment-accordion-finished-additional-assignments"
									(page)="onContactsPage(additional.id, $event, 'assignment-accordion-finished-additional-assignments')"
								/>
							</div>
						}
					</lvr-assignment-accordion-item>
				}
			}

			@if (finishedFdsAssignments().length > 0) {
				@for (item of finishedFdsAssignments(); track item.id) {
					<lvr-assignment-accordion-item
						[expanded]="shouldOpenMain(item)"
						[toggleOnIconOnly]="toggleOnIconOnly()"
						[assignmentId]="item.id"
						[phaseId]="item.phaseId"
						[isServiceOfficeStatement]="true"
						[disableTitleNavigation]="item.isRestricted"
						[disableTitleNavigationTooltip]="'base.button.assignment_details_disabled' | transloco"
						[chipLabel]="
							!isAssignmentOwner(item)
								? (item.createdByUser?.displayName ?? ('assignment.additional.unknown_user' | transloco))
								: null
						"
					>
						@let fdsTitle = getAccordionTitleKey(item);
						@let fdsSponsor = getSponsorName(item);
						<span ngProjectAs="accordion-title">{{ fdsTitle.key | transloco: fdsTitle.params }}</span>
						<span ngProjectAs="accordion-date"
							>{{ getPhaseStartDate(item) | date: 'dd.MM.yyyy' }}
							@if (fdsSponsor) {
								| {{ fdsSponsor }}
							}
						</span>

						<section>
							<div class="contact-headline">
								<span>{{ 'contact.contact_view.all_contacts' | transloco: { count: getContactCount(item) } }}</span>
							</div>
							@if (isContactsLoading(item)) {
								<p class="contacts-state">Kontakte werden geladen...</p>
							} @else if (hasContactsError(item)) {
								<p class="contacts-state contacts-state-error">Kontakte konnten nicht geladen werden.</p>
							}
						</section>
						@for (
							contact of visibleContacts(item.id, item.contacts, 'assignment-accordion-finished-fds');
							track contact.id
						) {
							@if (canReadContactsRestricted()) {
								<lvr-contact-card
									[showEdit]="item.phaseId === 6 && isAssignmentOwner(item)"
									[contact]="contact"
									[showRemarks]="false"
									[showDocuments]="true"
									[clientId]="clientId()"
								></lvr-contact-card>
							} @else {
								<a class="link" [routerLink]="[item.id, 'contacts', contact.id]">
									<lvr-contact-card
										[showEdit]="item.phaseId === 6 && isAssignmentOwner(item)"
										[contact]="contact"
										[showRemarks]="false"
										[showDocuments]="true"
										[clientId]="clientId()"
									></lvr-contact-card>
								</a>
							}
						}
						@if (item.contacts.length > 0) {
							<div class="contacts-footer">
								<lvr-button
									variant="primary"
									[label]="'base.button.export_contacts' | transloco"
									[ariaLabel]="'base.button.export_pdf' | transloco"
									[loadingLabel]="'base.button.generating_pdf' | transloco"
									[loading]="isExportLoadingFor(item.id)()"
									[disabled]="!canExportContacts(item)"
									[type]="'button'"
									(clicked)="export(item.id)"
									icon="ios_share"
								></lvr-button>
								<lvr-paginator
									class="paginator"
									[showFirstLastButtons]="showFirstLastButtons"
									[length]="item.contacts.length"
									[pageIndex]="pageIndexFor(item.id, 'assignment-accordion-finished-fds')"
									[pageSize]="pageSizeFor(item.id, 'assignment-accordion-finished-fds')"
									preferenceKey="assignment-accordion-finished-fds"
									(page)="onContactsPage(item.id, $event, 'assignment-accordion-finished-fds')"
								/>
							</div>
						}
					</lvr-assignment-accordion-item>
				}
			}
		</lvr-section>
	</cdk-accordion>
}
